/**
 * What the first run can honestly offer *this* device, so that finishing it means reachable. F-141.
 *
 * Leo, on making notifications part of the gate: *"yes part of the gate but we must make sure that
 * we handle device specificity as part of onboarding… If the site was opened in a browser window in
 * a mobile, we should use this time to tell the user how to install the app as a home screen app,
 * and indicate that notifications require that… If on a desktop device, we encourage mobile install
 * in the same way, show a QR code… If the user chooses no notifications we must enable browser
 * based notifications, and indicate we will be sending email."* And, closing the desktop case:
 * *"If the user has no mobile or chooses to stay in desktop then browser notifications must work
 * regardless of the user having the page open or not."*
 *
 * **Four devices, four different truths, and the reason this is a module rather than four
 * conditions inside a component**: every one of them is a promise about whether a question will
 * arrive, and a screen that offers the wrong one does not look wrong. It looks like a button. The
 * learner presses it, the flow completes, and the failure surfaces days later as silence.
 *
 * | The learner is on | What can be offered | What "done" means |
 * | --- | --- | --- |
 * | Android or desktop with Web Push | the browser's own permission prompt | **a stored subscription**, or a declared decline |
 * | iOS in a tab | how to add nabu to the home screen | they have seen it; email carries the questions |
 * | Safari on a Mac with no `PushManager` | a browser that can, or the Dock on a macOS that has it | they have seen it; email carries the questions |
 * | a browser that has refused | the path through its own settings | email carries the questions |
 *
 * ## Push in a tab is not an iOS restriction, and this file is where that stops leaking
 *
 * `install/` exists because **iOS** has no install API and grants no push until a site is on the
 * home screen. That is one platform's policy and it is not a general rule: Chrome, Edge and
 * Firefox on a desktop subscribe an ordinary tab with nothing installed, and — the part that
 * matters — the service worker in `packages/web/public/sw.js` receives a push and raises a
 * notification **with every nabu tab closed**, because a push wakes the worker rather than the
 * page. A flow that asked a desktop learner to install something before it would call them
 * reachable would be charging them for iOS's restriction. `support()` in `push.ts` already answers
 * `needs_install` only for an Apple device that is not standalone, so the gate is not there today;
 * this table is what keeps it from being added.
 *
 * ## The prompt fires once, ever
 *
 * `Notification.requestPermission()` is one-shot per origin. A learner who dismisses it cannot be
 * asked again by calling it a second time — the browser answers `denied` without showing anything —
 * so there is no retry to offer and a button that appeared to retry would be a lie. Two things
 * follow, and they are the screen's obligations rather than this module's: say what is about to
 * happen *before* the control is pressed, and have somewhere honest to go afterwards. `ask:
 * 'settings'` is that somewhere.
 *
 * ## Declining is a supported answer
 *
 * Every account signs in with a code mailed to an address it proved, so **there is always an email
 * address**, and F-122 already sends the question there when push cannot reach. So a learner who
 * declines is still reachable and the screen says so in as many words. What the gate must never be
 * is impassable: see `floor`.
 */
import { offersInstall, type InstallRoute } from '../install/route.js';
import type { PushSupport } from '../push.js';

/**
 * The browser's answer about notifications, including the case where there is nothing to ask.
 *
 * Written as `NotificationPermission | 'unavailable'` rather than as four literals, because that is
 * exactly what `permission()` in `push.ts` returns and the two must not drift: a fifth value added
 * to the DOM type would otherwise arrive here as a silent `default`. `unavailable` is its own value
 * rather than folded into `default` because on iOS in a tab `Notification` is not defined at all,
 * and reading that as "has not been asked yet" is what produces a button that opens no prompt and
 * then reports the silence as a refusal.
 */
export type PushPermission = NotificationPermission | 'unavailable';

/** What the decision needs to know, so a test can state a device without being one. */
export interface ReachFacts {
  /** What `installRoute` says can be offered here. Reused rather than re-derived. */
  install: InstallRoute;
  /** What `support()` says this browser can do about Web Push. */
  support: PushSupport;
  /** What the browser itself says, when there is a `Notification` to ask. */
  permission: PushPermission;
  /** A subscription exists in this browser **and** the service has been told about it. */
  subscribed: boolean;
  /** A device the learner carries. A coarse pointer, or any Apple handheld. */
  handheld: boolean;
  /**
   * Safari on a Mac, which is the awkward one.
   *
   * It is not iOS — there is no home screen — but recent macOS Safari raises web notifications only
   * for a site added to the Dock, and older ones cannot at all. Told apart so the copy can name the
   * menu item rather than telling a Mac to add something to a home screen it does not have.
   */
  desktopSafari: boolean;
}

/** The one control this step offers, and there is never more than one. */
export type ReachAsk =
  /** Ask the browser now. The only control that can end with a stored subscription. */
  | 'permission'
  /** No permission exists to ask for until nabu is on the home screen. iOS, in a tab. */
  | 'install'
  /** Safari on a Mac: the site has to be in the Dock first. */
  | 'dock'
  /** The browser has refused and only its own settings can undo that. */
  | 'settings'
  /** Nothing to offer: already subscribed, or a browser with no notifications at all. */
  | 'none';

/** What this step puts on the screen, and what it will have achieved. */
export interface ReachPlan {
  ask: ReachAsk;
  /** Show the install funnel beside the ask. It chooses its own wording from its own route. */
  install: boolean;
  /** Offer the address as a QR, to get nabu onto a phone. */
  qr: boolean;
  /**
   * Which channel carries a question if the learner finishes the step exactly as it stands.
   *
   * **`push` only when a subscription actually exists.** Not when permission was granted, not when
   * the learner pressed the button — the distinction is the whole point. Permission without a
   * stored subscription is a browser that would show a notification nobody will ever send it, and
   * the account's `PUSH#` row is the only thing the sweep can address. A screen that said
   * "notifications are on" off the back of a granted permission would be wrong in the one direction
   * that produces silence.
   *
   * There is no third value and there must not be. Every account proved an email address to sign in
   * and F-122 mails the question when push cannot reach, so `email` is a floor rather than a
   * failure — which is what lets the gate be finishable on a device that physically cannot grant
   * push, without the gate ever being a lie.
   */
  floor: 'push' | 'email';
}

/**
 * What to offer, in order, and the order is the argument.
 *
 * `subscribed` wins over everything: a device that is already receiving notifications must not be
 * offered a button to turn them on, which is the clearest possible sign nothing is reading the
 * browser. `needs_install` comes next because on iOS in a tab there is no permission to ask for at
 * all, and offering one there is the button that does nothing. `denied` before `ready` because a
 * refused browser will answer a second request without showing anybody anything.
 *
 * **The install card is offered on handhelds and not on desktops, and the QR the other way round.**
 * Not because a desktop cannot install — Chromium can — but because the two are answers to
 * different questions. On a phone, installing is how the notifications get better (and on iOS, how
 * they exist). On a laptop, the app belongs on the phone the learner will actually answer from, and
 * a second install offer on the desktop is a third thing on a screen that is asking for one.
 */
export function reachPlan(facts: ReachFacts): ReachPlan {
  const funnel = facts.handheld && offersInstall(facts.install);
  const qr = !facts.handheld;

  if (facts.subscribed) return { ask: 'none', install: false, qr, floor: 'push' };
  if (facts.support === 'needs_install') {
    return { ask: 'install', install: true, qr: false, floor: 'email' };
  }
  if (facts.permission === 'denied') {
    return { ask: 'settings', install: funnel, qr, floor: 'email' };
  }
  /*
   * An unsupported browser, and the one case worth telling apart from it.
   *
   * **Safari on a Mac is pointed at its own menu, and the copy has to hedge about which macOS.**
   * `PushManager` is absent in Safari only before macOS Ventura, and "Add to Dock" arrived in
   * Sonoma — so a Mac that lands here is, strictly, running an OS that has neither. The sentence
   * therefore leads with the browsers that work today and names the Dock as the other route, on
   * the versions that have it; pointing somebody straight at a File menu item their OS does not
   * carry is the wrong-instruction failure `install/route.ts` was written against, arriving on a
   * different platform. It is still worth telling apart from a generic "this browser cannot",
   * because the remedy on a Mac is real and the generic sentence names none.
   */
  if (facts.support === 'unsupported') {
    return { ask: facts.desktopSafari ? 'dock' : 'none', install: funnel, qr, floor: 'email' };
  }
  return { ask: 'permission', install: funnel, qr, floor: 'email' };
}

/**
 * The plan for a device nothing could be established about.
 *
 * **It exists so that the screen can always be drawn**, which on a step behind a closed gate is a
 * correctness property rather than a nicety. `reachFacts` reads the account and the browser, and
 * either can fail: `get` in `api.ts` has no timeout, so a request that never settles — a captive
 * portal, a stalled mobile connection, a hung edge — leaves a caller awaiting it for ever, and a
 * screen with no plan has no continue button on it. Every other route redirects back to that
 * screen. The learner is then looking at one muted line and a product that will not let them go
 * anywhere, which is the one thing this flow may never be.
 *
 * Nothing is offered and nothing is promised: we could not establish that this device can be
 * pushed to, so the honest answer is the one an unsupported browser gets. `floor: 'email'` is true
 * whatever the device turns out to be, because every account proved an address to sign in with and
 * F-122 mails the question when push cannot reach. The QR is dropped rather than guessed, since
 * `handheld` is among the things that could not be read and offering a phone its own address is
 * the confusion the flag exists to prevent.
 */
export const UNKNOWN_DEVICE: ReachPlan = {
  ask: 'none',
  install: false,
  qr: false,
  floor: 'email',
};

/**
 * Whether pressing the one control could end with a stored subscription on this device.
 *
 * The screen's continue button reads differently on either side of this, and so does the sentence
 * above it: where a subscription is still possible the step says so and waits, and where it is not
 * it says what will carry the questions instead. Its own function because the two call sites are a
 * label and a paragraph, and a label that promises what the paragraph denies is how a learner comes
 * to believe the product is broken.
 */
export function canSubscribeHere(plan: ReachPlan): boolean {
  return plan.ask === 'permission';
}
